import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { HelpButton } from '../components/ui/help-button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  ClipboardList, 
  FileText, 
  CheckSquare, 
  BarChart3,
  Play,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Clock,
  Loader2,
  Target,
  Award,
  HelpCircle,
  RotateCcw
} from 'lucide-react';
import { getUserErrorMessage } from '../utils/errorMessages';
import { toast } from 'sonner';
import WizardStepper from '../components/WizardStepper';
import WizardNavButtons from '../components/WizardNavButtons';
import { Coachmark } from '../components/Coachmark';
import { SteekproefBanner } from '../components/SteekproefBanner';

const ProcessPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [availableFrameworks, setAvailableFrameworks] = useState([]);
  const [showStartModal, setShowStartModal] = useState(false);
  const [activeQuestionnaire, setActiveQuestionnaire] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [currentNotes, setCurrentNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [completedResult, setCompletedResult] = useState(null);
  const [moduleStats, setModuleStats] = useState(null);

  const [showHelp, setShowHelp] = useState(false);
  const [revising, setRevising] = useState(null);
  const [viewAnswers, setViewAnswers] = useState(null);
  const [viewAnswersData, setViewAnswersData] = useState([]);
  const [steekproefCtx, setSteekproefCtx] = useState(null);

  // Context help texts per control category
  const getCategoryHelp = (category) => ({
    governance: 'Governance betreft het beleid, de verantwoordelijkheden en de bestuursstructuur rondom informatiebeveiliging. Denk aan: is er een CISO aangesteld? Is er een informatiebeveiligingsbeleid goedgekeurd door het management?',
    people: 'Mensen-controls gaan over bewustwording, training en gedrag van medewerkers. Denk aan: worden medewerkers getraind? Is er een onboarding/offboarding proces? Weten medewerkers hoe ze incidenten melden?',
    technology: 'Technologie-controls gaan over de technische beveiliging van systemen en netwerken. Denk aan: is MFA ingeschakeld? Worden patches tijdig geïnstalleerd? Is data versleuteld?',
    supply_chain: 'Supply chain-controls gaan over het beheer van risico\'s bij leveranciers en derde partijen. Denk aan: worden leveranciers geaudit? Is er een beleid voor toegang door derden?',
    general: 'Algemene controls betreffende het overkoepelende informatiebeveiligingsbeleid en risicobeheer van de organisatie.',
  }[category] || 'Beoordeel deze control op basis van de huidige situatie in uw organisatie.');

  const getControlHelp = (controlId) => {
    const helps = {
      'A5.1': 'Informatiebeveiligingsbeleid: Is er een formeel, door het management goedgekeurd beleidsdocument dat de aanpak van informatiebeveiliging beschrijft?',
      'A5.2': 'Rollen en verantwoordelijkheden: Zijn er duidelijke rollen (CISO, DPO, etc.) toegewezen met expliciete taken en bevoegdheden?',
      'A5.7': 'Risicobeoordeling: Wordt er bij nieuwe systemen, processen of wijzigingen een risicobeoordeling uitgevoerd om beveiligingsrisico\'s te identificeren?',
      'A6.1': 'Screening: Worden achtergrondcontroles uitgevoerd bij nieuwe medewerkers, zoals VOG, referentiechecks of diploma-verificatie?',
      'A6.3': 'Bewustwording: Ontvangen medewerkers regelmatig security awareness training? Dit omvat ook phishing-simulaties en social engineering tests.',
      'A6.5': 'Offboarding: Worden bij vertrek van medewerkers alle accounts afgesloten, toegangen ingetrokken en bedrijfsmiddelen geretourneerd?',
      'A7.1': 'Clean desk: Is er een beleid dat medewerkers verplicht hun werkplek op te ruimen en gevoelige informatie niet onbeheerd achter te laten?',
      'A8.1': 'Dataclassificatie: Wordt data geclassificeerd naar gevoeligheid en worden passende beschermingsmaatregelen toegepast?',
      'A8.2': 'Toegangsbeheer: Worden toegangsrechten regelmatig gereviewed en bijgewerkt op basis van het "need-to-know" principe?',
      'A8.5': 'MFA: Is multi-factor authenticatie ingeschakeld voor alle gebruikers, vooral voor toegang op afstand en privileged accounts?',
      'A8.7': 'Endpoint protection: Is antivirus/EDR actief op alle endpoints (laptops, servers, mobiele apparaten)?',
      'A8.8': 'Patchmanagement: Worden beveiligingspatches binnen een vastgestelde termijn (bijv. 30 dagen voor kritiek) geïnstalleerd?',
      'A8.13': 'Back-up: Worden back-ups regelmatig gemaakt, versleuteld opgeslagen en periodiek getest op herstelbaarheid?',
      'A8.15': 'Logging & monitoring: Is er centraal logging aanwezig en worden logs actief geanalyseerd op verdachte activiteiten?',
      'A8.20': 'Netwerksegmentatie: Is het netwerk gesegmenteerd zodat een aanval op één segment niet direct het hele netwerk treft?',
    };
    return helps[controlId] || null;
  };

  const handleReviseQuestionnaire = async (questionnaireId) => {
    if (!window.confirm('Weet u zeker dat u deze vragenlijst opnieuw wilt invullen? Alle eerdere antwoorden worden gewist.')) return;
    setRevising(questionnaireId);
    try {
      await api.post(`/process/questionnaires/${questionnaireId}/revise`);
      toast.success('Vragenlijst geopend voor herziening');
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De herziening kon niet worden gestart. Probeer het later opnieuw.'));
    } finally { setRevising(null); }
  };

  const handleViewAnswers = async (questionnaire) => {
    try {
      const res = await api.get(`/process/questionnaires/${questionnaire.id}`);
      setViewAnswersData(res.data?.answers || []);
      setViewAnswers(questionnaire);
    } catch {
      toast.error('De antwoorden konden niet worden geladen. Ververs de pagina.');
    }
  };

  const getAnswerLabel = (answer) => {
    const labels = { yes: 'Ja', partial: 'Gedeeltelijk', no: 'Nee', unknown: 'Onbekend' };
    return labels[answer] || answer;
  };

  const getAnswerColor = (answer) => {
    const colors = { yes: 'bg-green-100 text-green-700', partial: 'bg-amber-100 text-amber-700', no: 'bg-red-100 text-red-700', unknown: 'bg-neutral-100 text-neutral-600' };
    return colors[answer] || 'bg-neutral-100 text-neutral-600';
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, questionnairesRes, frameworksRes, peopleS, processS, techS, spCtx] = await Promise.all([
        api.get('/process/stats'),
        api.get('/process/questionnaires'),
        api.get('/process/questionnaires/available'),
        api.get('/people/stats').catch(() => ({ data: { completed: 0, total: 10, score: 0 } })),
        api.get('/process/stats').catch(() => ({ data: { completed: 0, total: 10, score: 0 } })),
        api.get('/technology/stats').catch(() => ({ data: { completed: 0, total: 10, score: 0 } })),
        api.get('/assessments/active-reassessment-context').catch(() => ({ data: { active: false } })),
      ]);
      setStats(statsRes.data);
      setQuestionnaires(questionnairesRes.data);
      setAvailableFrameworks(frameworksRes.data);
      setModuleStats({
        people: peopleS.data || { completed: 0, total: 10, score: 0 },
        process: processS.data || { completed: 0, total: 10, score: 0 },
        technology: techS.data || { completed: 0, total: 10, score: 0 },
      });
      setSteekproefCtx(spCtx.data);
    } catch (error) {
      toast.error('De gegevens konden niet worden geladen. Ververs de pagina.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuestionnaire = async (frameworkId) => {
    try {
      const response = await api.post('/process/questionnaires/start', {
        framework_id: frameworkId
      });
      
      const questionnaireRes = await api.get(`/process/questionnaires/${response.data.id}`);
      setActiveQuestionnaire(questionnaireRes.data);
      setCurrentQuestion(questionnaireRes.data.current_question);
      setShowStartModal(false);
      
      if (response.data.resumed) {
        toast.success('Vragenlijst hervat');
      } else {
        toast.success('Vragenlijst gestart');
      }
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De vragenlijst kon niet worden gestart. Probeer het later opnieuw.'));
    }
  };

  const handleResumeQuestionnaire = async (questionnaireId) => {
    try {
      const response = await api.get(`/process/questionnaires/${questionnaireId}`);
      setActiveQuestionnaire(response.data);
      setCurrentQuestion(response.data.current_question);
    } catch (error) {
      toast.error('De vragenlijst kon niet worden geladen. Ververs de pagina.');
    }
  };

  const handleGoToPreviousQuestion = async () => {
    if (!activeQuestionnaire) return;
    try {
      const response = await api.post(`/process/questionnaires/${activeQuestionnaire.id}/previous`);
      setCurrentQuestion(response.data.current_question);
      setActiveQuestionnaire({
        ...activeQuestionnaire,
        answered_count: response.data.answered_count,
        current_question_index: response.data.current_question_index
      });
      // Pre-fill the previous answer if available
      if (response.data.previous_answer) {
        setCurrentAnswer(response.data.previous_answer.answer || '');
        setCurrentNotes(response.data.previous_answer.notes || '');
      } else {
        setCurrentAnswer('');
        setCurrentNotes('');
      }
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Kan niet terug naar de vorige vraag.'));
    }
  };

  const handleSubmitAnswer = async () => {
    if (!currentAnswer) {
      toast.error('Selecteer een antwoord');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/process/questionnaires/${activeQuestionnaire.id}/answer`, {
        question_id: currentQuestion.id,
        answer: currentAnswer,
        notes: currentNotes || null
      });

      if (response.data.is_completed) {
        setCompletedResult({
          score: response.data.score,
          questionnaire: activeQuestionnaire
        });
        setActiveQuestionnaire(null);
        setCurrentQuestion(null);
        setShowResultsModal(true);
        fetchData();
      } else {
        setCurrentQuestion(response.data.next_question);
        setActiveQuestionnaire({
          ...activeQuestionnaire,
          answered_count: activeQuestionnaire.answered_count + 1,
          current_question_index: activeQuestionnaire.current_question_index + 1
        });
      }
      
      setCurrentAnswer('');
      setCurrentNotes('');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Uw antwoord kon niet worden opgeslagen. Probeer het opnieuw.'));
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      not_started: 'border-neutral-200 bg-neutral-50 text-neutral-600',
      in_progress: 'border-orange-200 bg-orange-50 text-orange-700',
      completed: 'border-green-200 bg-green-50 text-green-700',
    };
    const labels = {
      not_started: 'Niet gestart',
      in_progress: 'Bezig',
      completed: 'Afgerond',
    };
    return (
      <Badge className={variants[status] || variants.not_started}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 40) return 'text-orange-500';
    return 'text-red-500';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      governance: 'Governance',
      people: 'Mensen',
      technology: 'Technologie',
      supply_chain: 'Supply Chain',
      general: 'Algemeen'
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </MainLayout>
    );
  }

  // Active questionnaire view
  if (activeQuestionnaire && currentQuestion) {
    const progress = ((activeQuestionnaire.answered_count + 1) / activeQuestionnaire.total_questions) * 100;
    
    return (
      <MainLayout>
        <div className="p-6 lg:p-8 max-w-3xl mx-auto">
          {/* Progress Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-neutral-600">
                {activeQuestionnaire.framework_name}
              </p>
              <p className="text-sm text-neutral-500">
                Vraag {activeQuestionnaire.answered_count + 1} van {activeQuestionnaire.total_questions}
              </p>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Question Card */}
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-8">
              {/* Category Badge */}
              <Badge variant="outline" className="mb-4">
                {getCategoryLabel(currentQuestion.category)}
              </Badge>

              {/* Question */}
              <h2 className="text-xl font-semibold text-black mb-6">
                {currentQuestion.question}
              </h2>

              {/* Control Reference + Help */}
              <div className="flex items-center gap-2 mb-6">
                <p className="text-xs text-neutral-400">
                  Control: {currentQuestion.control_id}
                </p>
                <button
                  onClick={() => setShowHelp(!showHelp)}
                  className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-black transition-colors"
                  data-testid="question-help-toggle"
                >
                  <HelpCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {showHelp ? 'Verberg uitleg' : 'Meer informatie'}
                </button>
              </div>

              {/* Contextual Help Panel */}
              {showHelp && (
                <div className="mb-6 p-4 rounded-lg bg-neutral-50 border border-neutral-200 space-y-3" data-testid="question-help-panel">
                  {getControlHelp(currentQuestion.control_id) && (
                    <p className="text-xs text-neutral-700 leading-relaxed">
                      <strong>Over deze control:</strong> {getControlHelp(currentQuestion.control_id)}
                    </p>
                  )}
                  <p className="text-xs text-neutral-600 leading-relaxed">
                    <strong>Categorie:</strong> {getCategoryHelp(currentQuestion.category)}
                  </p>
                  <p className="text-[10px] text-neutral-500 mt-1">
                    Tip: Kies "Gedeeltelijk" als u het proces wel heeft maar het niet volledig of consistent wordt toegepast.
                  </p>
                </div>
              )}

              {/* Answer Options */}
              <RadioGroup value={currentAnswer} onValueChange={setCurrentAnswer} className="space-y-3">
                {[
                  { value: 'yes', label: 'Ja', description: 'Volledig geïmplementeerd' },
                  { value: 'partial', label: 'Gedeeltelijk', description: 'Deels geïmplementeerd' },
                  { value: 'no', label: 'Nee', description: 'Niet geïmplementeerd' },
                  { value: 'unknown', label: 'Onbekend', description: 'Geen informatie beschikbaar' },
                ].map((option) => (
                  <div
                    key={option.value}
                    className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-all ${
                      currentAnswer === option.value
                        ? 'border-black bg-neutral-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    }`}
                    onClick={() => setCurrentAnswer(option.value)}
                  >
                    <RadioGroupItem value={option.value} id={option.value} />
                    <Label htmlFor={option.value} className="flex-1 cursor-pointer">
                      <span className="font-medium text-black">{option.label}</span>
                      <span className="text-sm text-neutral-500 ml-2">{option.description}</span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              {/* Notes */}
              <div className="mt-6">
                <Label htmlFor="notes" className="text-sm text-neutral-600">
                  Notities (optioneel)
                </Label>
                <Textarea
                  id="notes"
                  value={currentNotes}
                  onChange={(e) => setCurrentNotes(e.target.value)}
                  placeholder="Voeg eventuele opmerkingen toe..."
                  className="mt-2 resize-none"
                  rows={3}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-between mt-8">
                <div className="flex items-center gap-2">
                  {activeQuestionnaire.answered_count > 0 && (
                    <Button
                      variant="outline"
                      onClick={handleGoToPreviousQuestion}
                      data-testid="previous-question-btn"
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" strokeWidth={1.5} />
                      Vorige
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    className="text-neutral-500"
                    onClick={() => {
                      setActiveQuestionnaire(null);
                      setCurrentQuestion(null);
                    }}
                    data-testid="save-later-btn"
                  >
                    Opslaan & Later
                  </Button>
                </div>
                <Button
                  className="bg-black text-white hover:bg-neutral-800"
                  onClick={handleSubmitAnswer}
                  disabled={submitting || !currentAnswer}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Volgende
                      <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-white">
        <div className="mx-auto max-w-5xl px-6 py-8 space-y-10">
        <WizardStepper moduleStats={moduleStats} />
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">
              Process Module
            </p>
            <h1 className="text-2xl font-bold tracking-tight text-black">
              Compliance Vragenlijsten
            </h1>
            <p className="text-sm text-neutral-600 mt-1">
              Evalueer uw organisatieprocessen en beleidsrichtlijnen
            </p>
          </div>
          <div className="flex items-center gap-3">
            <HelpButton steps={[
              { title: 'Vragenlijsten', description: 'Beantwoord compliance-vragenlijsten per framework om uw procesvolwassenheid te meten.', target: '[data-testid="start-questionnaire-button"]' },
              { title: 'Statistieken', description: 'De vier kaarten tonen het totaal, voltooid, lopend en gemiddelde score van uw vragenlijsten.', target: null },
              { title: 'Resultaten', description: 'Bekijk per voltooide vragenlijst de score en antwoorden, of start een revisie.', target: null },
            ]} />
            <Button 
              className="bg-black text-white hover:bg-neutral-800"
              onClick={() => setShowStartModal(true)}
              data-testid="start-questionnaire-button"
            >
              <Play className="mr-2 h-4 w-4" strokeWidth={1.5} />
              Start Vragenlijst
            </Button>
          </div>
        </div>

        {/* Coachmark */}
        <Coachmark
          id="process-intro"
          text="Kies een framework (bijv. NIS2 of ISO 27001) en beantwoord de vragenlijst. Je kunt meerdere frameworks invullen."
        />

        {/* Steekproef Banner */}
        {steekproefCtx?.active && (
          <SteekproefBanner
            active={true}
            moduleName="process"
            moduleLabel="Organisatie"
            reassessment={steekproefCtx.reassessment}
            nulmetingSnapshot={steekproefCtx.nulmeting_snapshot}
            isModuleIncluded={(steekproefCtx.reassessment?.modules || []).includes('process') || (steekproefCtx.reassessment?.modules || []).includes('all')}
          />
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <ClipboardList className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{stats?.total_questionnaires || 0}</p>
                  <p className="text-xs text-neutral-500">Totaal vragenlijsten</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{stats?.completed || 0}</p>
                  <p className="text-xs text-neutral-500">Afgerond</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-orange-50 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-orange-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{stats?.in_progress || 0}</p>
                  <p className="text-xs text-neutral-500">In uitvoering</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Target className="h-5 w-5 text-purple-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${stats?.latest_score ? getScoreColor(stats.latest_score) : 'text-neutral-400'}`}>
                    {stats?.latest_score ?? '-'}%
                  </p>
                  <p className="text-xs text-neutral-500">Laatste score</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Questionnaires List */}
        <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">
              Vragenlijsten
            </CardTitle>
          </CardHeader>
          <CardContent>
            {questionnaires.length > 0 ? (
              <div className="divide-y divide-neutral-100">
                {questionnaires.map((q) => (
                  <div key={q.id} className="flex items-center gap-4 py-4">
                    <div className="h-10 w-10 rounded-lg bg-neutral-100 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-neutral-600" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-black">{q.framework_name}</p>
                      <p className="text-xs text-neutral-500">
                        {q.answered_count}/{q.total_questions} vragen beantwoord
                      </p>
                    </div>
                    <div className="text-right">
                      {getStatusBadge(q.status)}
                      {q.status === 'completed' && q.score !== null && (
                        <p className={`text-lg font-bold mt-1 ${getScoreColor(q.score)}`}>
                          {q.score}%
                        </p>
                      )}
                    </div>
                    {q.status === 'in_progress' && (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-neutral-200 text-neutral-600 hover:text-black"
                          onClick={() => handleViewAnswers(q)}
                          data-testid={`view-progress-${q.id}`}
                        >
                          <FileText className="h-3 w-3 mr-1" strokeWidth={1.5} />
                          Bekijk
                        </Button>
                        <Button
                          size="sm"
                          className="bg-black text-white hover:bg-neutral-800"
                          onClick={() => handleResumeQuestionnaire(q.id)}
                        >
                          Hervat
                          <ArrowRight className="ml-1 h-3 w-3" strokeWidth={1.5} />
                        </Button>
                      </div>
                    )}
                    {q.status === 'completed' && (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-neutral-200 text-neutral-600 hover:text-black"
                          onClick={() => handleViewAnswers(q)}
                          data-testid={`view-answers-${q.id}`}
                        >
                          <FileText className="h-3 w-3 mr-1" strokeWidth={1.5} />
                          Bekijk
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-neutral-200 text-neutral-600 hover:text-black"
                          onClick={() => handleReviseQuestionnaire(q.id)}
                          disabled={revising === q.id}
                          data-testid={`revise-questionnaire-${q.id}`}
                        >
                          {revising === q.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <>
                              <RotateCcw className="h-3 w-3 mr-1" strokeWidth={1.5} />
                              Herzien
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center">
                <ClipboardList className="h-12 w-12 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-lg font-medium text-black mb-2">Geen vragenlijsten</h3>
                <p className="text-sm text-neutral-500 mb-4">
                  Start uw eerste compliance vragenlijst om uw beleid te evalueren.
                </p>
                <Button 
                  className="bg-black text-white hover:bg-neutral-800"
                  onClick={() => setShowStartModal(true)}
                >
                  <Play className="mr-2 h-4 w-4" strokeWidth={1.5} />
                  Start Vragenlijst
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Volgende stap */}
        <section className="rounded-2xl border border-neutral-200 p-6" data-testid="process-next-step">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <p className="spk-section-label mb-0">Volgende stap</p>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black text-white">Aanbevolen</span>
              </div>
              <h3 className="text-lg font-semibold mt-1">Ga verder met Systemen</h3>
              <p className="text-sm text-neutral-600 mt-1">Scan je infrastructuur op kwetsbaarheden en bekijk beveiligingsrisico's.</p>
            </div>
            <Button
              onClick={() => window.location.href = '/technology'}
              className="bg-black text-white hover:bg-neutral-800 shrink-0"
              data-testid="process-goto-technology"
            >
              Systemen
              <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
            </Button>
          </div>
        </section>

        <WizardNavButtons />
      </div>
      </div>

      {/* Start Modal */}
      <Dialog open={showStartModal} onOpenChange={setShowStartModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Kies een Framework</DialogTitle>
            <DialogDescription>
              Selecteer het compliance framework waarvoor u de vragenlijst wilt invullen.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {availableFrameworks.map((framework) => (
              <button
                key={framework.id}
                onClick={() => handleStartQuestionnaire(framework.id)}
                className="w-full p-4 text-left border border-neutral-200 rounded-lg hover:border-black hover:bg-neutral-50 transition-all"
                data-testid={`framework-option-${framework.id}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium text-black">{framework.name}</h4>
                    <p className="text-sm text-neutral-500 mt-1">{framework.description}</p>
                  </div>
                  <Badge variant="outline">{framework.question_count} vragen</Badge>
                </div>
              </button>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStartModal(false)}>
              Annuleren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Answers Modal */}
      <Dialog open={!!viewAnswers} onOpenChange={(open) => { if (!open) setViewAnswers(null); }}>
        <DialogContent className="sm:max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader className="pb-3 border-b border-neutral-100">
            <DialogTitle className="text-base font-semibold">Antwoorden: {viewAnswers?.framework_name}</DialogTitle>
            <DialogDescription className="text-sm text-neutral-500">
              {viewAnswers?.answered_count} vragen beantwoord • Score: {viewAnswers?.score}%
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1" data-testid="view-answers-popup">
            {viewAnswersData.length > 0 ? viewAnswersData.map((a, i) => (
              <div key={i} className="p-4 bg-neutral-50 rounded-xl border border-neutral-100">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <p className="text-xs font-mono text-neutral-400 mb-1">{a.control_id || `Vraag ${i + 1}`}</p>
                    <p className="text-sm font-medium text-black">{a.question}</p>
                  </div>
                  <Badge className={`shrink-0 ${getAnswerColor(a.answer)}`}>
                    {getAnswerLabel(a.answer)}
                  </Badge>
                </div>
                {a.notes && (
                  <div className="mt-2 pt-2 border-t border-neutral-200">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Notitie</p>
                    <p className="text-xs text-neutral-600 italic">{a.notes}</p>
                  </div>
                )}
              </div>
            )) : (
              <p className="text-sm text-neutral-400 text-center py-6">Geen antwoorden beschikbaar</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Results Modal */}
      <Dialog open={showResultsModal} onOpenChange={setShowResultsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-green-600" strokeWidth={1.5} />
              Vragenlijst Afgerond!
            </DialogTitle>
          </DialogHeader>
          
          {completedResult && (
            <div className="py-6 text-center">
              <div className={`text-6xl font-bold mb-2 ${getScoreColor(completedResult.score)}`}>
                {completedResult.score}%
              </div>
              <p className="text-neutral-600">
                Uw compliance score voor {completedResult.questionnaire?.framework_name}
              </p>
              
              <div className="mt-6 p-4 bg-neutral-50 rounded-lg text-left">
                <p className="text-sm text-neutral-600">
                  {completedResult.score >= 70 ? (
                    <>✅ Uitstekend! Uw organisatie voldoet grotendeels aan de eisen.</>
                  ) : completedResult.score >= 40 ? (
                    <>⚠️ Redelijk. Er zijn verbeterpunten die aandacht nodig hebben.</>
                  ) : (
                    <>🔴 Kritiek. Er zijn significante gaps in uw compliance.</>
                  )}
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              className="w-full bg-black text-white hover:bg-neutral-800"
              onClick={() => setShowResultsModal(false)}
            >
              Sluiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default ProcessPage;
